import React, { useState, useEffect, useRef } from 'react';
import { getStudentDailyLearningData } from "../../../network/Endpoint";
import smile1 from '../../../assets/images/smile-1.png';
import smile2 from '../../../assets/images/smile-2.png';
import smile3 from '../../../assets/images/smile-3.png';
import grade1 from '../../../assets/images/grade_1.png';
import grade2 from '../../../assets/images/grade_2.png';
import grade3 from '../../../assets/images/grade_3.png';
import grade4 from '../../../assets/images/grade_4.png';
import grade5 from '../../../assets/images/grade_5.png';
import modeLesson from '../../../assets/images/mode_lesson.png';
import modeHomeWork from '../../../assets/images/mode_homework.png';
import modeDaily from '../../../assets/images/mode_daily.png';
import modeFree from '../../../assets/images/mode_free.png';
import modeChallenge from '../../../assets/images/mode_challeng.png';
import icoSchool from '../../../assets/images/School_ico.png';
import icoStudy from '../../../assets/images/study_ico.png';
import memo from "../../../assets/images/s_w_Memo.png";
import accuracy from "../../../assets/images/s_w_Accuracy.png";
import clock from "../../../assets/images/s_w_Clock.png";
import * as d3 from 'd3';
import iconSmile from "../../../assets/images/icon-smile.svg";
import iconSmile2 from "../../../assets/images/icon-smile2.svg";
import iconSmile3 from "../../../assets/images/icon-smile3.svg";

const StudentDailyResults = ({ studentId, selectedDate, onBackToWeekly, initialLoading = false }) => {
  const [dailyLoading, setDailyLoading] = useState(initialLoading);
  const [showLoadingUI, setShowLoadingUI] = useState(false); // 로딩 UI 표시 여부
  const loadingTimerRef = useRef(null); // 로딩 타이머 참조
  const [dailyData, setDailyData] = useState(null);
  const [dailyRecords, setDailyRecords] = useState([]);
  const [totalStudyTime, setTotalStudyTime] = useState(0);
  const [averageAccuracy, setAverageAccuracy] = useState(0);

  // 차트를 위한 ref 추가
  const countChartRef = useRef(null);
  const accuracyChartRef = useRef(null);
  const timeChartRef = useRef(null);
  const legendContainerRef = useRef(null);

  // 로딩 상태 변경 시 지연된 로딩 UI 표시 처리
  useEffect(() => {
    // 로딩이 시작되면 타이머 설정
    if (dailyLoading) {
      // 기존 타이머 정리
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
      
      // 0.3초 지연 후 로딩 UI 표시
      loadingTimerRef.current = setTimeout(() => {
        if (dailyLoading) {
          setShowLoadingUI(true);
        }
      }, 300);
    } else {
      // 로딩이 끝나면 타이머 정리하고 로딩 UI 숨김
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowLoadingUI(false);
    }
    
    // 컴포넌트 언마운트 시 타이머 정리
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [dailyLoading]);

  // 특정 날짜의 일일 학습 데이터 불러오기
  useEffect(() => {
    if (!selectedDate || !studentId) return;
    
    const fetchDailyData = async () => {
      setDailyLoading(true);
      try {
        const response = await getStudentDailyLearningData({
          studentId,
          params: {
            date: selectedDate
          }
        });
        
        if (response.data && response.data.status.code === "Success") {
          setDailyData(response.data.data);
          setDailyRecords(response.data.data.thisDateRecords || []);
          setTotalStudyTime(response.data.data.totalStudyTime);
          setAverageAccuracy(response.data.data.averageAccuracy);
        }
      } catch (error) {
        console.error("일일 학습 결과를 가져오는 중 오류 발생:", error);
      } finally {
        setDailyLoading(false);
      }
    };
    
    // 로딩 시작 시 타이머 초기화 (기존 타이머가 있다면)
    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }
    
    // initialLoading 설정과 관계없이 항상 0.3초 후에만 로딩 UI 표시
    // 로딩 UI는 useEffect([dailyLoading])에서 관리됨
    
    fetchDailyData();
  }, [selectedDate, studentId]);

  // 모드별 차트 렌더링 - 데이터 로드 완료 후
  useEffect(() => {
    if (!dailyLoading && dailyData) {
      createCommonLegend();
      createCountChart();
      createAccuracyChart();
      createTimeChart();
    }
  }, [dailyData, dailyLoading]);

  // 공통 모드 색상 및 레이블 정의
  const modeColors = {
    'LESSON': '#7494FF', // 학교 수업 - 파란색
    'HOMEWORK': '#FF8481', // 학교 숙제 - 빨간색
    'DAILY': '#D699FF', // 오늘의 학습 - 보라색
    'CHALLENGE': '#FFD67D', // 일프로 도전 - 노란색
    'FREE': '#74CEB2'  // 자유 학습 - 초록색
  };
  
  const modeLabels = {
    'LESSON': '학교 수업',
    'HOMEWORK': '학교 숙제',
    'DAILY': '오늘의 학습',
    'CHALLENGE': '일프로 도전', 
    'FREE': '자유 학습'
  };
  
  // 표시 순서 정의
  const orderPriority = ['LESSON', 'HOMEWORK', 'DAILY', 'CHALLENGE', 'FREE'];

  // 공통 범례 생성
  const createCommonLegend = () => {
    if (!legendContainerRef.current) return;
    
    // 기존 내용 삭제
    d3.select(legendContainerRef.current).selectAll("*").remove();
    
    const legendContainer = d3.select(legendContainerRef.current)
      .append("div")
      .style("display", "flex")
      .style("justify-content", "center")
      .style("margin-bottom", "10px");
      
    // 모든 모드에 대해 범례 아이템 생성 (값이 없어도 표시)
    orderPriority.forEach((mode, index) => {
      const legendItem = legendContainer
        .append("div")
        .style("display", "flex")
        .style("align-items", "center")
        .style("margin-right", index === orderPriority.length - 1 ? "0" : "12px");
        
      // 색상 원형 아이콘
      legendItem.append("div")
        .style("width", "6px")
        .style("height", "6px")
        .style("border-radius", "50%")
        .style("background-color", modeColors[mode])
        .style("margin-right", "4px");
        
      // 모드 레이블
      legendItem.append("div")
        .style("font-size", "12px")
        .style("color", "#50555F")
        .text(modeLabels[mode]);
    });
  };

  // 학습량 차트 생성
  const createCountChart = () => {
    if (!countChartRef.current || !dailyData?.recordCountsByMode) return;
    
    // 기존 SVG 삭제
    d3.select(countChartRef.current).selectAll("*").remove();
    
    // 데이터 필터링 및 정렬
    const data = Object.entries(dailyData.recordCountsByMode)
      .filter(([mode, value]) => value !== null && value !== undefined)
      .map(([mode, count]) => ({
        mode,
        value: count,
        label: modeLabels[mode],
        color: modeColors[mode]
      }))
      .sort((a, b) => {
        return orderPriority.indexOf(a.mode) - orderPriority.indexOf(b.mode);
      });

    if (data.length === 0) return;
    
    // SVG 크기 설정
    const margin = { top: 30, right: 15, bottom: 50, left: 40 };
    const width = 300 - margin.left - margin.right;
    const height = 280 - margin.top - margin.bottom;
    
    // SVG 생성
    const svg = d3.select(countChartRef.current)
      .append("svg")
      .attr("width", 300)
      .attr("height", 280)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // X축 스케일 설정
    const x = d3.scaleBand()
      .domain(data.map(d => d.mode))
      .range([0, width])
      .padding(0.2);
    
    // Y축 스케일 설정
    const maxValue = d3.max(data, d => d.value);
    const yMax = Math.ceil(maxValue / 5) * 5;
    const y = d3.scaleLinear()
      .domain([0, yMax])
      .range([height, 0]);
    
    // 수평 그리드 라인 그리기
    svg.selectAll("line.horizontalGrid")
      .data(y.ticks(5))
      .enter()
      .append("line")
      .attr("class", "horizontalGrid")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // Y축 눈금과 레이블 설정
    svg.selectAll(".y-label")
      .data(y.ticks(5))
      .enter()
      .append("text")
      .attr("class", "y-label")
      .attr("x", -10)
      .attr("y", d => y(d))
      .attr("dy", "0.32em")
      .attr("text-anchor", "end")
      .attr("fill", "#50555F")
      .style("font-size", "12px")
      .text(d => d + "개");
    
    // X축 그리기 (바닥 선만)
    svg.append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", height)
      .attr("y2", height)
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // 막대 너비 설정
    const barWidth = 20;
    
    // 최소 막대 높이 (픽셀)
    const minBarHeight = 8;
    
    // 막대 그리기 - 둥근 상단 적용 (반원)
    svg.selectAll(".bar")
      .data(data)
      .enter()
      .append("path")
      .attr("class", "bar")
      .attr("d", d => {
        if (d.value === 0) return "";
        
        const xCenter = x(d.mode) + x.bandwidth() / 2;
        const x0 = xCenter - barWidth / 2;
        
        // 값이 0보다 크고 아주 작을 때도 최소 높이 보장
        const calculatedHeight = height - y(d.value);
        const barHeight = Math.max(calculatedHeight, minBarHeight);
        const y0 = height - barHeight;
        const radius = barWidth / 2;
        
        return `
          M ${x0},${height}
          L ${x0},${y0 + radius}
          A ${radius} ${radius} 0 0 1 ${x0 + barWidth},${y0 + radius}
          L ${x0 + barWidth},${height}
          Z
        `;
      })
      .attr("fill", d => d.color);
    
    // 값 표시
    svg.selectAll(".bar-value")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "bar-value")
      .attr("x", d => x(d.mode) + x.bandwidth() / 2)
      .attr("y", d => {
        if (d.value === 0) return y(0) - 10;
        
        const calculatedHeight = height - y(d.value);
        const barHeight = Math.max(calculatedHeight, minBarHeight);
        return height - barHeight - 8; // 막대 위에 텍스트 배치
      })
      .attr("text-anchor", "middle")
      .attr("fill", "#50555F")
      .style("font-size", "12px")
      .style("font-weight", "500")
      .text(d => d.value);
    
    // 타이틀 하단에 추가
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", height + 30)
      .attr("text-anchor", "middle")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .style("font-weight", "500")
      .text("학습량");
  };

  // 정확도 차트 생성
  const createAccuracyChart = () => {
    if (!accuracyChartRef.current || !dailyData?.accuracyByMode) return;
    
    // 기존 SVG 삭제
    d3.select(accuracyChartRef.current).selectAll("*").remove();
    
    // 데이터 필터링 및 정렬
    const data = Object.entries(dailyData.accuracyByMode)
      .filter(([mode, value]) => value !== null && value !== undefined)
      .map(([mode, accuracy]) => ({
        mode,
        value: accuracy,
        label: modeLabels[mode],
        color: modeColors[mode]
      }))
      .sort((a, b) => {
        return orderPriority.indexOf(a.mode) - orderPriority.indexOf(b.mode);
      });

    if (data.length === 0) return;
    
    // SVG 크기 설정
    const margin = { top: 30, right: 15, bottom: 50, left: 40 };
    const width = 300 - margin.left - margin.right;
    const height = 280 - margin.top - margin.bottom;
    
    // SVG 생성
    const svg = d3.select(accuracyChartRef.current)
      .append("svg")
      .attr("width", 300)
      .attr("height", 280)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // X축 스케일 설정
    const x = d3.scaleBand()
      .domain(data.map(d => d.mode))
      .range([0, width])
      .padding(0.2);
    
    // Y축 스케일 설정 - 0에서 100까지
    const y = d3.scaleLinear()
      .domain([0, 100])
      .range([height, 0]);
    
    // 수평 그리드 라인 그리기
    svg.selectAll("line.horizontalGrid")
      .data(y.ticks(5))
      .enter()
      .append("line")
      .attr("class", "horizontalGrid")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // Y축 눈금과 레이블 설정
    svg.selectAll(".y-label")
      .data(y.ticks(5))
      .enter()
      .append("text")
      .attr("class", "y-label")
      .attr("x", -10)
      .attr("y", d => y(d))
      .attr("dy", "0.32em")
      .attr("text-anchor", "end")
      .attr("fill", "#50555F")
      .style("font-size", "12px")
      .text(d => d + "%");
    
    // X축 그리기 (바닥 선만)
    svg.append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", height)
      .attr("y2", height)
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // 막대 너비 설정
    const barWidth = 20;
    
    // 최소 막대 높이 (픽셀)
    const minBarHeight = 8;
    
    // 막대 그리기 - 둥근 상단 적용
    svg.selectAll(".bar")
      .data(data)
      .enter()
      .append("path")
      .attr("class", "bar")
      .attr("d", d => {
        if (d.value === 0) return "";
        
        const xCenter = x(d.mode) + x.bandwidth() / 2;
        const x0 = xCenter - barWidth / 2;
        
        // 값이 0보다 크고 아주 작을 때도 최소 높이 보장
        const calculatedHeight = height - y(d.value);
        const barHeight = Math.max(calculatedHeight, minBarHeight);
        const y0 = height - barHeight;
        const radius = barWidth / 2;
        
        return `
          M ${x0},${height}
          L ${x0},${y0 + radius}
          A ${radius} ${radius} 0 0 1 ${x0 + barWidth},${y0 + radius}
          L ${x0 + barWidth},${height}
          Z
        `;
      })
      .attr("fill", d => d.color);
    
    // 값 표시 - 소수점에서 자연수로 반올림
    svg.selectAll(".bar-value")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "bar-value")
      .attr("x", d => x(d.mode) + x.bandwidth() / 2)
      .attr("y", d => {
        if (d.value === 0) return y(0) - 10;
        
        const calculatedHeight = height - y(d.value);
        const barHeight = Math.max(calculatedHeight, minBarHeight);
        return height - barHeight - 8; // 막대 위에 텍스트 배치
      })
      .attr("text-anchor", "middle")
        .attr("fill", "#50555F")
        .style("font-size", "12px")
        .style("font-weight", "500")
      .text(d => Math.round(d.value));
    
    // 타이틀 하단에 추가
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", height + 30)
      .attr("text-anchor", "middle")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .style("font-weight", "500")
      .text("정확도");
  };

  // 학습시간 차트 생성
  const createTimeChart = () => {
    if (!timeChartRef.current || !dailyData?.studyTimeByMode) return;
    
    // 기존 SVG 삭제
    d3.select(timeChartRef.current).selectAll("*").remove();
    
    // 데이터 필터링 및 정렬
    const data = Object.entries(dailyData.studyTimeByMode)
      .filter(([mode, value]) => value !== null && value !== undefined)
      .map(([mode, seconds]) => ({
        mode,
        value: seconds / 60, // 초를 분으로 변환 (소수점 유지)
        seconds: seconds,
        label: modeLabels[mode],
        color: modeColors[mode]
      }))
      .sort((a, b) => {
        return orderPriority.indexOf(a.mode) - orderPriority.indexOf(b.mode);
      });

    if (data.length === 0) return;
    
    // SVG 크기 설정
    const margin = { top: 30, right: 15, bottom: 50, left: 40 };
    const width = 300 - margin.left - margin.right;
    const height = 280 - margin.top - margin.bottom;
    
    // SVG 생성
    const svg = d3.select(timeChartRef.current)
      .append("svg")
      .attr("width", 300)
      .attr("height", 280)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);
    
    // X축 스케일 설정
    const x = d3.scaleBand()
      .domain(data.map(d => d.mode))
      .range([0, width])
      .padding(0.2);
    
    // Y축 스케일 설정 - 동적 간격 계산
    const maxTime = d3.max(data, d => d.value);
    
    // 최대값에 따른 눈금 간격 결정
    let yMax, yStep;
    if (maxTime < 5) {
      // 5분 미만: 1분 간격
      yMax = Math.max(Math.ceil(maxTime), 5);
      yStep = 1;
    } else if (maxTime < 10) {
      // 10분 미만: 2분 간격
      yMax = Math.ceil(maxTime / 2) * 2;
      yStep = 2;
    } else if (maxTime < 30) {
      // 30분 미만: 5분 간격
      yMax = Math.ceil(maxTime / 5) * 5;
      yStep = 5;
    } else {
      // 30분 이상: 10분 간격
      yMax = Math.ceil(maxTime / 10) * 10;
      yStep = 10;
    }
    
    const y = d3.scaleLinear()
      .domain([0, yMax])
      .range([height, 0]);
    
    // 수평 그리드 라인 그리기
    const yTicks = d3.range(0, yMax + yStep, yStep);
    svg.selectAll("line.horizontalGrid")
      .data(yTicks)
      .enter()
      .append("line")
      .attr("class", "horizontalGrid")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => y(d))
      .attr("y2", d => y(d))
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // Y축 눈금과 레이블 설정
    svg.selectAll(".y-label")
      .data(yTicks)
      .enter()
      .append("text")
      .attr("class", "y-label")
      .attr("x", -10)
      .attr("y", d => y(d))
      .attr("dy", "0.32em")
      .attr("text-anchor", "end")
      .attr("fill", "#50555F")
      .style("font-size", "12px")
      .text(d => d + "분");
    
    // X축 그리기 (바닥 선만)
    svg.append("line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", height)
      .attr("y2", height)
      .attr("stroke", "#E8E8E8")
      .attr("stroke-width", 1);
    
    // 막대 너비 설정
    const barWidth = 20;
    
    // 최소 막대 높이 (픽셀)
    const minBarHeight = 8;
    
    // 막대 그리기 - 둥근 상단 적용, 최소 높이 보장
    svg.selectAll(".bar")
      .data(data)
      .enter()
      .append("path")
      .attr("class", "bar")
      .attr("d", d => {
        if (d.seconds === 0) return "";
        
        const xCenter = x(d.mode) + x.bandwidth() / 2;
        const x0 = xCenter - barWidth / 2;
        
        // 값이 0보다 크고 아주 작을 때도 최소 높이 보장
        const calculatedHeight = height - y(d.value);
        const barHeight = Math.max(calculatedHeight, minBarHeight);
        const y0 = height - barHeight;
        const radius = barWidth / 2;
        
        return `
          M ${x0},${height}
          L ${x0},${y0 + radius}
          A ${radius} ${radius} 0 0 1 ${x0 + barWidth},${y0 + radius}
          L ${x0 + barWidth},${height}
          Z
        `;
      })
      .attr("fill", d => d.color);
    
    // 값 표시 - 막대 위에 위치하도록 조정
    svg.selectAll(".bar-value")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "bar-value")
      .attr("x", d => x(d.mode) + x.bandwidth() / 2)
      .attr("y", d => {
        if (d.seconds === 0) return y(0) - 10;
        
        const calculatedHeight = height - y(d.value);
        const barHeight = Math.max(calculatedHeight, minBarHeight);
        return height - barHeight - 8; // 막대 위에 텍스트 배치
      })
      .attr("text-anchor", "middle")
        .attr("fill", "#50555F")
        .style("font-size", "12px")
        .style("font-weight", "500")
      .text(d => {
        const min = Math.floor(d.seconds / 60);
        const sec = d.seconds % 60;
        
        if (min === 0) {
          return `${sec}초`;
        } else if (min >= 10) {
          return `${min}분`;
        } else {
          return `${min}분${sec > 0 ? sec + '초' : ''}`;
        }
      });
    
    // 타이틀 하단에 추가
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", height + 30)
      .attr("text-anchor", "middle")
      .attr("fill", "#50555F")
      .style("font-size", "16px")
      .style("font-weight", "500")
      .text("학습 시간");
  };

  const formatStudyTimeDaily = (seconds) => {
    if (!seconds || seconds === 0) return '0초';
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes < 1) {
      return `${remainingSeconds}초`;
    } else if (minutes >= 10) {
      return `${minutes}분`;
    }
    
    return `${minutes}분 ${remainingSeconds > 0 ? remainingSeconds + '초' : ''}`;
  };
  
  const formatDateStr = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    
    const date = new Date(dateTimeStr);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${month}월${day}일\n${hours}:${minutes}`;
  };
  
  const getModeImage = (sourceType) => {
    switch (sourceType) {
      case 'LESSON':
        return modeLesson;
      case 'HOMEWORK':
        return modeHomeWork;
      case 'DAILY':
        return modeDaily;
      case 'FREE':
        return modeFree;
      case 'CHALLENGE':
        return modeChallenge;
      default:
        return modeLesson;
    }
  };

  // 로딩 UI - 0.3초 이상 로딩 시에만 표시
  if (showLoadingUI) {
    return (
      <div className="relative">
        <div className="fixed inset-0 bg-white bg-opacity-70 flex justify-center items-center z-50">
          <div className="flex flex-col items-center bg-white p-6 rounded-lg shadow-md">
            <div className="w-12 h-12 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-700">일일 학습 데이터를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // 데이터가 없고 로딩 중인 경우에는 로딩 중 화면 표시
  if (!dailyData && dailyLoading) {
    return (
      <div className="max-w-[1044px] mx-auto pb-8">
        <div className="bg-white p-[22px] rounded-lg shadow-lg mb-8 min-h-[500px] flex justify-center items-center">
          <div className="w-8 h-8 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }
  
  // 데이터가 없고 로딩 중이 아닌 경우에만 오류 메시지 표시
  if (!dailyData && !dailyLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <p className="text-gray-500">데이터를 불러올 수 없습니다.</p>
      </div>
    );
  }

  const schoolStudyRecords = dailyRecords.filter(record => 
    record.sourceType === 'HOMEWORK' || record.sourceType === 'LESSON'
  );
  
  const selfStudyRecords = dailyRecords.filter(record => 
    record.sourceType !== 'HOMEWORK' && record.sourceType !== 'LESSON'
  );

  return (
    <div className="max-w-[1044px] mx-auto pb-8">
      <div className="bg-white p-[22px] rounded-lg shadow-lg mb-8">
          {/*<button*/}
          {/*    onClick={onBackToWeekly}*/}
          {/*    className="flex items-center text-blue-600 hover:text-blue-800 self-start mb-4 absolute"*/}
          {/*>*/}
          {/*  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">*/}
          {/*    <path fillRule="evenodd"*/}
          {/*          d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"*/}
          {/*          clipRule="evenodd"/>*/}
          {/*  </svg>*/}
          {/*  주간 학습 결과로 돌아가기*/}
          {/*</button>*/}
          <h2 className="text-[24px] text-[#50555F] font-bold text-center w-full mb-6 mt-5">
            {selectedDate && (() => {
              const dateObj = new Date(selectedDate);
              const month = dateObj.getMonth() + 1;
              const day = dateObj.getDate();
              return `${month}월 ${day}일 학습 현황`;
            })()}
          </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-11 mt-14">
          {/* 학습량 */}
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              <img src={memo} alt="학습량" className="w-[104px] h-[104px] mr-9"/>
              <div>
                <h3 className="text-[24px] font-[500] text-[#50555F]">학습량</h3>
                <div className="text-[34px] font-[700] text-[#355BDF]">{dailyRecords.length}<span
                    className="text-[24px] ml-1">개</span></div>
              </div>
            </div>
          </div>

          {/* 정확도 */}
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              <img src={accuracy} alt="정확도" className="w-[104px] h-[104px] mr-9"/>
              <div>
                <h3 className="text-[24px] font-[500] text-[#50555F]">정확도</h3>
                <div className="text-[34px] font-[700] text-[#355BDF]">{Math.round(averageAccuracy) || 0}<span
                    className="text-[24px] ml-1">%</span></div>
              </div>
            </div>
          </div>

          {/* 학습시간 */}
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              <img src={clock} alt="학습시간" className="w-[104px] h-[104px] mr-9"/>
              <div>
                <h3 className="text-[24px] font-[500] text-[#50555F]">학습시간</h3>
                <div className="text-[34px] font-[700] text-[#355BDF]">
                  {Math.floor(totalStudyTime / 60) > 0 ? Math.floor(totalStudyTime / 60) : "00"}
                  <span className="text-[24px]">분</span>
                  {totalStudyTime % 60 > 0 ? totalStudyTime % 60 : "00"}
                  <span className="text-[24px]">초</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t pt-8 mb-8">
          <h3 className="text-[24px] font-[700] text-[#50555F] text-center mb-10">학습 모드별 현황</h3>
          
          {/* 모드별 색상 범례 - 모든 모드 표시 */}
          <div className="flex justify-start mb-8 ml-8">
            {orderPriority.map((mode, index) => (
              <div key={mode} className="flex items-center mx-4">
                <div 
                  className="rounded-full mr-2" 
                  style={{ 
                    backgroundColor: modeColors[mode],
                    width: '6px',
                    height: '6px'
                  }}
                ></div>
                <span className="text-[12px] text-[#50555F] font-[500] " style={{ fontSize: '12px' }}>{modeLabels[mode]}</span>
              </div>
            ))}
          </div>
          
          <div className="flex flex-row justify-center gap-2">
            <div className="flex flex-col items-center">
              <div ref={countChartRef} className="w-[320px] h-[280px]"></div>
            </div>
            <div className="flex flex-col items-center">
              <div ref={accuracyChartRef} className="w-[320px] h-[280px]"></div>
            </div>
            <div className="flex flex-col items-center">
              <div ref={timeChartRef} className="w-[320px] h-[280px]"></div>
            </div>
          </div>
        </div>
        <div className="border-t pt-8 mb-8">
            <h3 className="text-[24px] font-[700] text-[#50555F] text-center mb-10">학습 모드별 상세결과</h3>


        {/* 학습 기록이 없을 때 */}
        {dailyRecords.length === 0 && (
            <div className="flex flex-col items-center justify-center p-10 bg-gray-50 rounded-lg">
              <div className="text-[20px] text-gray-500 mb-4">해당 일자에 학습 기록이 없습니다.</div>
              <div className="text-[16px] text-gray-400">학습을 완료하면 여기에 결과가 표시됩니다.</div>
            </div>
        )}

        {/* 학교 학습 영역 */}
        {dailyRecords.length > 0 && (
            <div className="mb-8 mx-[5px]">
              <div className="flex mb-8 ml-12 gap-1">
                <img className="flex items-center w-[32px] h-[32px]" src={icoSchool} alt="school icon"/>
                <div className="flex items-end text-[20px] font-[700] text-[#50555F]">학교 학습</div>
              </div>

              {schoolStudyRecords.length > 0 ? (
                  <div className="overflow-x-auto rounded-t-[20px] overflow-hidden">
                    <table className="min-w-full bg-white border rounded-lg table-fixed">
                      <thead
                          className="bg-[#F3F7FF] text-[18px] font-[700] text-[#9BA5C2] h-[56px] border border-[#F3F7FF]">
                        <tr className="border-b-0 text-center ">
                          <th className="py-3 w-[70px]">순번</th>
                          <th className="py-3 w-[110px]">학습 일시</th>
                          <th className="py-3 w-[110px]">학습 모드</th>
                          <th className="py-3 w-[130px]">차시 단원명</th>
                          <th className="py-3 w-[300px]">차시명/스테이지명</th>
                          <th className="py-3 w-[100px]">학습 시간</th>
                          <th className="py-3 w-[80px]">등급</th>
                          <th className="py-3 w-[90px]">정확도</th>
                        </tr>
                      </thead>
                      <tbody>
                                                    {schoolStudyRecords
                                  .sort((a, b) => {
                                    // 1. 모드 우선순위 정의 (LESSON 먼저)
                                    const priorityMap = {'LESSON': 1, 'HOMEWORK': 2};
                                    const priorityA = priorityMap[a.sourceType] || 99;
                                    const priorityB = priorityMap[b.sourceType] || 99;

                                    // 2. 우선순위로 정렬
                                    if (priorityA !== priorityB) {
                                      return priorityA - priorityB;
                                    }

                                    // 3. 같은 우선순위면 날짜로 정렬
                                    return new Date(a.dateTime) - new Date(b.dateTime);
                                  })
                                  .map((record, index, array) => {
                                    // 현재 레코드의 sourceType
                                    const currentSourceType = record.sourceType;

                                    // 이전 레코드의 sourceType (없으면 null)
                                    const prevSourceType = index > 0 ? array[index - 1].sourceType : null;

                                    // 새로운 모드 섹션의 시작인지 확인
                                    const isNewSection = currentSourceType !== prevSourceType;

                                    // 현재 섹션 내에서의 순번 계산
                                    const sectionRecords = array.filter((r, i) => i <= index && r.sourceType === currentSourceType);
                                    const sectionIndex = sectionRecords.length;

                                    return (
                                        <tr
                                            key={index}
                                            className={`${isNewSection && index > 0 ? 'border-t-2 border-gray-300' : ''}`}
                                        >
                                          <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[16px] text-[#5E6169]">{sectionIndex}</td>
                                          <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[14px] text-[#5E6169] whitespace-pre-line">{formatDateStr(record.dateTime)}</td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center">
                                    <div className="flex justify-center">
                                      <img
                                          src={getModeImage(record.sourceType)}
                                          alt={record.sourceType}
                                      />
                                    </div>
                                  </td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center truncate text-[14px] text-[#5E6169]"
                                      title={record.lessonChapterTitle}>{record.lessonChapterTitle}</td>
                                  <td className="py-3 px-2 border-b border-r border-[#E9E9E9] text-center">
                                    <div className="truncate w-[284px] text-[14px] text-[#5E6169]"
                                         title={record.lessonTitle}>{record.lessonTitle}</div>
                                    <div className="truncate w-[284px] text-[14px] text-[#5E6169]"
                                         title={record.stageName}>{record.stageName}</div>
                                  </td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[14px] text-[#5E6169]">{formatStudyTimeDaily(record.studySeconds || 0)}</td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center">
                                    {record.grade && (
                                        <div className="flex items-center justify-center">
                                          <img
                                              src={
                                                record.grade === 1 ? grade1 :
                                                    record.grade === 10 ? grade2 :
                                                        record.grade === 30 ? grade3 :
                                                            record.grade === 50 ? grade4 :
                                                                record.grade === 100 ? grade5 : grade1
                                              }
                                              alt={`Grade ${record.grade}`}
                                          />
                                        </div>
                                    )}
                                  </td>
                                  <td className="py-1 px-1 border-b border-[#E9E9E9] text-center text-[#5E6169] text-[14px] font-[700]">
                                    {record.accuracy ? (
                                        <div className="flex flex-col items-center justify-center">
                                          <img
                                              src={
                                                record.accuracy >= 90 ? iconSmile :
                                                    record.accuracy >= 70 ? iconSmile2 :
                                                        iconSmile3
                                              }
                                              alt="accuracy icon"
                                              className="w-8 h-8"
                                          />
                                          <span
                                              style={{
                                                color: record.accuracy >= 90 ? '#7494FF' :
                                                    record.accuracy >= 70 ? '#00B079' :
                                                        '#EF7070'
                                              }}
                                          >
                                                  {record.accuracy.toFixed(0)}%
                                                </span>
                                        </div>
                                    ) : '-'}
                                  </td>
                                </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
              ) : (
                  <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg">
                    <div className="text-[18px] text-gray-500">해당 일자에 학교 학습 기록이 없습니다.</div>
                  </div>
              )}
            </div>
        )}
        {/* 자율 학습 영역 */}
        {dailyRecords.length > 0 && (
            <div className="mb-8 mx-[5px] pt-8">
              <div className="flex items-center">
                <div className="flex mb-8 ml-12 gap-1">
                  <img className="flex items-center w-[32px] h-[32px]" src={icoStudy} alt="study icon"/>
                  <div className="flex items-end text-[20px] font-[700] text-[#50555F]">자율 학습</div>
                </div>
              </div>

              {selfStudyRecords.length > 0 ? (
                  <div className="overflow-x-auto rounded-t-[20px] overflow-hidden">
                    <table className="min-w-full bg-white border rounded-lg table-fixed">
                      <thead
                          className="bg-[#F3F7FF] text-[18px] font-[700] text-[#9BA5C2] h-[56px] border border-[#F3F7FF]">
                      <tr className="border-b-0 text-center">
                        <th className="py-3 w-[70px]">순번</th>
                        <th className="py-3 w-[110px]">학습 일시</th>
                        <th className="py-3 w-[110px]">학습 모드</th>
                        <th className="py-3 w-[130px]">차시 단원명</th>
                        <th className="py-3 w-[300px]">차시명/스테이지명</th>
                        <th className="py-3 w-[100px]">학습 시간</th>
                        <th className="py-3 w-[80px]">등급</th>
                        <th className="py-3 w-[90px]">정확도</th>
                      </tr>
                      </thead>
                      <tbody>
                      {selfStudyRecords
                          .sort((a, b) => {
                            // 1. 모드 우선순위 정의
                            const priorityMap = {'DAILY': 1, 'FREE': 2, 'CHALLENGE': 3};
                            const priorityA = priorityMap[a.sourceType] || 99;
                            const priorityB = priorityMap[b.sourceType] || 99;

                            // 2. 우선순위로 정렬
                            if (priorityA !== priorityB) {
                              return priorityA - priorityB;
                            }

                            // 3. 같은 우선순위면 날짜로 정렬
                            return new Date(a.dateTime) - new Date(b.dateTime);
                          })
                          .map((record, index, array) => {
                            // 현재 레코드의 sourceType
                            const currentSourceType = record.sourceType;

                            // 이전 레코드의 sourceType (없으면 null)
                            const prevSourceType = index > 0 ? array[index - 1].sourceType : null;

                            // 새로운 모드 섹션의 시작인지 확인
                            const isNewSection = currentSourceType !== prevSourceType;

                            // 현재 섹션 내에서의 순번 계산
                            const sectionRecords = array.filter((r, i) => i <= index && r.sourceType === currentSourceType);
                            const sectionIndex = sectionRecords.length;

                            return (
                                <tr
                                    key={index}
                                    className={`${isNewSection && index > 0 ? 'border-t-2 border-gray-300' : ''}`}
                                >
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[16px] text-[#5E6169]">{sectionIndex}</td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[14px] text-[#5E6169] whitespace-pre-line">{formatDateStr(record.dateTime)}</td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center">
                                    <div className="flex justify-center">
                                      <img
                                          src={getModeImage(record.sourceType)}
                                          alt={record.sourceType}
                                      />
                                    </div>
                                  </td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center truncate text-[14px] text-[#5E6169]"
                                      title={`${record.level}레벨${record.chapterSeq}단원`}>{record.level}레벨{record.chapterSeq}단원</td>
                                  <td className="py-4 px-2 border-b border-r border-[#E9E9E9] text-center">
                                    <div className="truncate w-[284px] text-[14px] text-[#5E6169]"
                                         title={record.stageName}>{record.stageName}</div>
                                  </td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center text-[14px] text-[#5E6169]">{formatStudyTimeDaily(record.studySeconds || 0)}</td>
                                  <td className="py-4 px-1 border-b border-r border-[#E9E9E9] text-center">
                                    {record.grade && (
                                        <div className="flex items-center justify-center">
                                          <img
                                              src={
                                                record.grade === 1 ? grade1 :
                                                    record.grade === 10 ? grade2 :
                                                        record.grade === 30 ? grade3 :
                                                            record.grade === 50 ? grade4 :
                                                                record.grade === 100 ? grade5 : grade1
                                              }
                                              alt={`Grade ${record.grade}`}
                                          />
                                        </div>
                                    )}
                                  </td>
                                  <td className="py-1 px-1 border-b border-[#E9E9E9] text-center text-[#5E6169] text-[14px] font-[700]">
                                    {record.accuracy ? (
                                        <div className="flex flex-col items-center justify-center">
                                          <img
                                              src={
                                                record.accuracy >= 90 ? iconSmile :
                                                    record.accuracy >= 70 ? iconSmile2 :
                                                        iconSmile3
                                              }
                                              alt="accuracy icon"
                                              className="w-8 h-8"
                                          />
                                          <span
                                              style={{
                                                color: record.accuracy >= 90 ? '#7494FF' :
                                                    record.accuracy >= 70 ? '#00B079' :
                                                        '#EF7070'
                                              }}
                                          >
                                                  {record.accuracy.toFixed(0)}%
                                                </span>
                                        </div>
                                    ) : '-'}
                                  </td>
                                </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
              ) : (
                  <div className="flex flex-col items-center justify-center p-6 bg-gray-50 rounded-lg">
                    <div className="text-[18px] text-gray-500">해당 일자에 자율 학습 기록이 없습니다.</div>
                  </div>
              )}
            </div>
        )}
      </div>

      </div>
    </div>
  );
};

export default StudentDailyResults;